package com.fantasytenis.shared.data.skills.api

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.core.network.WrappedResponse
import com.fantasytenis.shared.data.skills.dto.*
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*

/**
 * Skills API client.
 *
 * Handles all skills-related HTTP requests (leaderboards, history, trends).
 */
class SkillsApi(
    private val httpClient: HttpClient,
    private val baseUrl: String
) {

    /**
     * Get skill leaderboard for a category.
     * GET /v2/public/tennis/skills/leaderboard
     */
    suspend fun getLeaderboard(
        category: String,
        gender: String? = null,
        limit: Int = 50,
        offset: Int = 0
    ): ApiResponse<SkillLeaderboardDataDto> {
        return try {
            val url = buildString {
                append("${baseUrl}public/tennis/skills/leaderboard")
                append("?category=$category")
                append("&limit=$limit")
                append("&offset=$offset")
                if (gender != null) {
                    append("&gender=$gender")
                }
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<SkillLeaderboardDataDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get leaderboard", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<SkillLeaderboardDataDto>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get leaderboard", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get player skill history.
     * GET /v2/public/tennis/skills/player/:id/history
     */
    suspend fun getPlayerHistory(
        playerId: String,
        weeks: Int = 8
    ): ApiResponse<PlayerSkillHistoryDataDto> {
        return try {
            val url = "${baseUrl}public/tennis/skills/player/$playerId/history?weeks=$weeks"
            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<PlayerSkillHistoryDataDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get player history", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<PlayerSkillHistoryDataDto>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get player history", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get trending players.
     * GET /v2/public/tennis/skills/trending
     */
    suspend fun getTrendingPlayers(
        direction: String = "up",
        limit: Int = 10
    ): ApiResponse<List<TrendingPlayerEntryDto>> {
        return try {
            val url = "${baseUrl}public/tennis/skills/trending?direction=$direction&limit=$limit"
            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<List<TrendingPlayerEntryDto>>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get trending players", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<List<TrendingPlayerEntryDto>>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get trending players", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get hidden gems.
     * GET /v2/public/tennis/skills/hidden-gems
     */
    suspend fun getHiddenGems(limit: Int = 10): ApiResponse<List<HiddenGemEntryDto>> {
        return try {
            val url = "${baseUrl}public/tennis/skills/hidden-gems?limit=$limit"
            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<List<HiddenGemEntryDto>>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get hidden gems", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<List<HiddenGemEntryDto>>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get hidden gems", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }
}
